/**
 * EventDetailScreen: the event page, shared by the Mini App and the studio website
 * (shared-client-screens D3, rows E1 to E7). Props only: each side reads its own event and hands
 * in Back, the chat card, the photo viewer and what the button does. The button is the shared
 * `eventCta` ladder (View booking, Sold out, Registration closed, Reserve your spot); the app
 * reserves here and shows "Reserving…" while it does, the website reserves at its checkout (E4).
 */
import type { ComponentType } from 'react';
import type { HubEventDetailDto, HubServiceDetailDto } from '@/api/types';
import { eventCta, eventMinutes } from '@/pages/booking/shared/bookingRules';
import { SessionDetailFrame } from './SessionDetailFrame';
import type { DetailChat } from './ServiceDetailScreen';
import { DetailGalleryView } from './components/DetailGalleryView';
import { DetailHero } from './components/DetailHero';
import type { DetailLightboxProps } from './components/DetailLightboxView';
import { DetailInfoRow } from './components/DetailInfoRow';
import { DetailProviderCardView } from './components/DetailProviderCardView';
import { DetailStickyBar } from './components/DetailStickyBar';
import { EventWhenBlock } from './components/EventWhenBlock';
import { eventPriceText, formatWhen, paymentTermsText, type PaymentTermsInput } from './detailFormat';
import { SafeRichText } from '@/components/booking/SafeRichText';
import styles from './SessionDetailPage.module.css';

export type EventDetailContent = Pick<
  HubEventDetailDto,
  | 'title'
  | 'summary'
  | 'description'
  | 'imageUrl'
  | 'focalPoint'
  | 'gallery'
  | 'startTime'
  | 'endTime'
  | 'modality'
  | 'spotsRemaining'
  | 'capacity'
  | 'isBookable'
  | 'cancellation'
> & {
  /** Dollars; null or 0 is free. */
  price: number | null;
  payment: PaymentTermsInput;
  location: HubServiceDetailDto['location'];
};

export interface EventDetailScreenProps {
  status: 'loading' | 'notfound' | 'ready';
  onBack: () => void;
  /** ready: the event. */
  event?: EventDetailContent;
  Lightbox: ComponentType<DetailLightboxProps>;
  chat: DetailChat | null;
  tz: string;
  /** The client already holds a spot: the button is View booking. */
  booked: boolean;
  onViewBooking: () => void;
  /** Reserve your spot: `reserving` while the app holds the spot; `enabled` false keeps it disabled. */
  reserve: { onReserve: () => void; reserving: boolean; enabled: boolean };
}

export function EventDetailScreen({ status, onBack, event, Lightbox, chat, tz, booked, onViewBooking, reserve }: EventDetailScreenProps) {
  if (status !== 'ready' || !event) {
    return (
      <SessionDetailFrame
        status={status === 'ready' ? 'loading' : status}
        pageTitle="Event Details"
        onBack={onBack}
        notFoundMessage="We couldn't find this event — it may have been removed."
      />
    );
  }

  const priceText = eventPriceText(event.price);
  const whenText = formatWhen(event.startTime, tz);
  // A viewer whose live hold the door keeps past the cutoff reads isBookable and keeps Reserve.
  const cta = eventCta({ booked, spotsRemaining: event.spotsRemaining, isBookable: event.isBookable, reserving: reserve.reserving, whenText });
  const onCta = cta.kind === 'view' ? onViewBooking : cta.kind === 'reserve' ? reserve.onReserve : () => {};

  return (
    <SessionDetailFrame
      status="ready"
      pageTitle="Event Details"
      onBack={onBack}
      hero={<DetailHero name={event.title} summary={event.summary} imageUrl={event.imageUrl} focalPoint={event.focalPoint} />}
      bottomBar={
        <DetailStickyBar
          priceMain={priceText}
          terms={paymentTermsText(event.payment, event.payment.paymentRequirement !== 'NONE' && event.price != null && event.price > 0, event.price)}
          ctaLabel={cta.label}
          ctaSub={cta.sub}
          onCta={onCta}
          disabled={cta.disabled || (cta.kind === 'reserve' && !reserve.enabled)}
        />
      }
    >
      <EventWhenBlock
        whenText={whenText}
        duration={eventMinutes(event.startTime, event.endTime)}
        modality={event.modality}
        priceText={priceText}
        spotsRemaining={event.spotsRemaining}
        capacity={event.capacity}
      />
      <div className={styles.divider} />
      {event.description && <SafeRichText html={event.description} className={styles.desc} />}
      <DetailGalleryView gallery={event.gallery} Lightbox={Lightbox} />
      {chat && <DetailProviderCardView businessName={chat.businessName} line={chat.line} onChat={chat.onChat} />}
      <DetailInfoRow location={event.location} cancellation={event.cancellation} />
    </SessionDetailFrame>
  );
}
